import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Calendar, ChevronRight, ChevronDown, Briefcase, Folder, 
  AlertTriangle, AlertCircle, RefreshCw, Filter, Users
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const PortfolioRoadmapPage = () => {
  const [loading, setLoading] = useState(true);
  const [roadmapData, setRoadmapData] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [filters, setFilters] = useState({
    portfolio_id: '',
    program_id: '',
    team: ''
  });
  const [expandedPrograms, setExpandedPrograms] = useState({});
  const [expandedProjects, setExpandedProjects] = useState({});
  const [projectDetails, setProjectDetails] = useState({});

  useEffect(() => {
    fetchFilterOptions();
    fetchRoadmap();
  }, []);

  useEffect(() => {
    fetchRoadmap();
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const [portfoliosRes, programsRes] = await Promise.all([
        api.get('/portfolio/roadmap/portfolios'),
        api.get('/portfolio/roadmap/programs')
      ]);
      setPortfolios(portfoliosRes.data.portfolios || []);
      setPrograms(programsRes.data.programs || []);
    } catch (error) {
      console.debug('Filter options not available');
    }
  };

  const fetchRoadmap = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.portfolio_id) params.append('portfolio_id', filters.portfolio_id);
      if (filters.program_id) params.append('program_id', filters.program_id);
      if (filters.team) params.append('team', filters.team);
      
      const response = await api.get(`/portfolio/roadmap?${params.toString()}`);
      setRoadmapData(response.data);
      
      // Expand all programs by default
      const expanded = {};
      response.data.programs?.forEach(p => {
        expanded[p.program_id] = true;
      });
      setExpandedPrograms(expanded);
    } catch (error) {
      console.error('Error fetching roadmap:', error);
      toast.error('Failed to load roadmap');
    } finally {
      setLoading(false);
    }
  };

  const toggleProgram = (programId) => {
    setExpandedPrograms(prev => ({
      ...prev,
      [programId]: !prev[programId]
    }));
  };

  const toggleProject = async (projectId) => {
    const isExpanding = !expandedProjects[projectId];
    
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
    
    // Fetch project details (epics/features) if expanding and not already loaded
    if (isExpanding && !projectDetails[projectId]) {
      try {
        const response = await api.get(`/portfolio/roadmap/project/${projectId}`);
        setProjectDetails(prev => ({
          ...prev,
          [projectId]: response.data
        }));
      } catch (error) {
        console.error('Error fetching project details:', error);
        toast.error('Failed to load project details');
      }
    }
  };

  const getHealthColor = (status) => {
    switch (status) {
      case 'green': return 'bg-green-500';
      case 'amber': return 'bg-amber-500';
      case 'red': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getHealthBadge = (status) => {
    switch (status) {
      case 'green': return <Badge className="bg-green-100 text-green-800">On Track</Badge>;
      case 'amber': return <Badge className="bg-amber-100 text-amber-800">At Risk</Badge>;
      case 'red': return <Badge variant="destructive">Critical</Badge>;
      default: return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const calculateBarPosition = (startDate, endDate, timeline) => {
    if (!startDate || !timeline?.start || !timeline?.end) {
      return { left: '0%', width: '100%' };
    }
    
    const timelineStart = new Date(timeline.start);
    const timelineEnd = new Date(timeline.end);
    const projectStart = new Date(startDate);
    const projectEnd = endDate ? new Date(endDate) : timelineEnd;
    
    const totalDays = (timelineEnd - timelineStart) / (1000 * 60 * 60 * 24);
    const startOffset = Math.max(0, (projectStart - timelineStart) / (1000 * 60 * 60 * 24));
    const duration = Math.max(1, (projectEnd - projectStart) / (1000 * 60 * 60 * 24));
    
    const left = (startOffset / totalDays) * 100;
    const width = Math.min((duration / totalDays) * 100, 100 - left);
    
    return { left: `${left}%`, width: `${width}%` };
  };

  const timeline = roadmapData?.timeline;
  const quarters = timeline?.quarters || [];

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--clr-brand-primary)]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-2">
            <Calendar className="w-7 h-7 text-[var(--clr-brand-primary)]" />
            Portfolio Roadmap
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">
            Timeline view of projects grouped by program
          </p>
        </div>
        <Button variant="outline" onClick={fetchRoadmap}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[var(--clr-text-secondary)]" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <Select 
              value={filters.portfolio_id || "all"} 
              onValueChange={(v) => setFilters(prev => ({ ...prev, portfolio_id: v === "all" ? "" : v }))}
            >
              <SelectTrigger className="w-48" data-testid="filter-portfolio">
                <SelectValue placeholder="All Portfolios" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Portfolios</SelectItem>
                {portfolios.map(p => (
                  <SelectItem key={p.portfolio_id} value={p.portfolio_id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select 
              value={filters.program_id || "all"} 
              onValueChange={(v) => setFilters(prev => ({ ...prev, program_id: v === "all" ? "" : v }))}
            >
              <SelectTrigger className="w-48" data-testid="filter-program">
                <SelectValue placeholder="All Programs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Programs</SelectItem>
                {programs.map(p => (
                  <SelectItem key={p.program_id} value={p.program_id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Timeline Header */}
      {quarters.length > 0 && (
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            <div className="flex border-b bg-[var(--clr-surface-subtle)] rounded-t-lg">
              <div className="w-64 flex-shrink-0 p-3 font-medium text-[var(--clr-text-secondary)]">
                Program / Project
              </div>
              <div className="flex-1 flex">
                {quarters.map((q, idx) => (
                  <div 
                    key={idx} 
                    className="flex-1 p-2 text-center text-sm font-medium text-[var(--clr-text-secondary)] border-l"
                  >
                    {q.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Roadmap Content */}
      <div className="overflow-x-auto">
        <div className="min-w-[800px] space-y-2">
          {/* Programs */}
          {roadmapData?.programs?.map((program) => (
            <Card key={program.program_id} className="overflow-hidden">
              {/* Program Header */}
              <div 
                className="flex items-center cursor-pointer hover:bg-[var(--clr-surface-subtle)] transition-colors"
                onClick={() => toggleProgram(program.program_id)}
              >
                <div className="w-64 flex-shrink-0 p-4 flex items-center gap-3">
                  {expandedPrograms[program.program_id] ? (
                    <ChevronDown className="w-5 h-5 text-[var(--clr-text-secondary)]" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-[var(--clr-text-secondary)]" />
                  )}
                  <Briefcase className="w-5 h-5 text-[var(--clr-brand-primary)]" />
                  <div>
                    <p className="font-medium text-[var(--clr-text-primary)]">{program.name}</p>
                    <p className="text-xs text-[var(--clr-text-secondary)]">
                      {program.total_projects} projects
                      {program.at_risk_count > 0 && (
                        <span className="text-red-600 ml-2">
                          • {program.at_risk_count} at risk
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex-1 h-12 relative bg-[var(--clr-surface-subtle)]">
                  {/* Aggregate bar could go here */}
                </div>
              </div>

              {/* Projects */}
              {expandedPrograms[program.program_id] && (
                <div className="border-t">
                  {program.projects?.map((project) => {
                    const barPos = calculateBarPosition(project.start_date, project.end_date, timeline);
                    const isExpanded = expandedProjects[project.project_id];
                    const details = projectDetails[project.project_id];
                    
                    return (
                      <div key={project.project_id}>
                        <div 
                          className="flex items-center border-b hover:bg-[var(--clr-surface-subtle)] transition-colors"
                        >
                          <div className="w-64 flex-shrink-0 p-3 pl-8 flex items-center gap-2">
                            <button
                              onClick={() => toggleProject(project.project_id)}
                              className="p-1 hover:bg-[var(--clr-surface-hover)] rounded"
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-[var(--clr-text-secondary)]" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-[var(--clr-text-secondary)]" />
                              )}
                            </button>
                            <Folder className="w-4 h-4 text-[var(--clr-text-secondary)]" />
                            <div className="flex-1 min-w-0">
                              <Link 
                                to={`/project/${project.project_id}`}
                                className="font-medium text-sm text-[var(--clr-text-primary)] hover:text-[var(--clr-brand-primary)] truncate block"
                              >
                                {project.name}
                              </Link>
                              <div className="flex items-center gap-2 mt-1">
                                <div className={`w-2 h-2 rounded-full ${getHealthColor(project.health_status)}`} />
                                <span className="text-xs text-[var(--clr-text-secondary)]">
                                  {project.phase}
                                </span>
                                {project.risk_count > 0 && (
                                  <span className="text-xs text-red-600 flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    {project.risk_count}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Timeline Bar */}
                          <div className="flex-1 h-14 relative px-2 py-2">
                            <div 
                              className={`absolute h-8 rounded ${getHealthColor(project.health_status)} opacity-80 flex items-center px-2`}
                              style={{ left: barPos.left, width: barPos.width, top: '50%', transform: 'translateY(-50%)' }}
                            >
                              <span className="text-xs text-white font-medium truncate">
                                {project.progress_percent}%
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Expanded Epics/Features with Gantt Bars */}
                        {isExpanded && details && (
                          <div className="bg-[var(--clr-surface-subtle)] border-b">
                            {details.epics?.length > 0 ? (
                              details.epics.map((epic) => {
                                const epicBarPos = calculateBarPosition(epic.start_date, epic.end_date, timeline);
                                const epicStatusColor = epic.status === 'Completed' ? 'bg-green-500' :
                                  epic.status === 'In Progress' ? 'bg-amber-500' :
                                  epic.status === 'Behind' ? 'bg-red-500' : 'bg-gray-400';
                                
                                return (
                                  <div key={epic.epic_id}>
                                    {/* Epic Row with Gantt Bar */}
                                    <div className="flex items-center border-b hover:bg-[var(--clr-surface-hover)]">
                                      <div className="w-64 flex-shrink-0 p-2 pl-12 flex items-center gap-2">
                                        <span className="text-xs font-medium px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                                          Epic
                                        </span>
                                        <span className="text-sm text-[var(--clr-text-primary)] truncate" title={epic.name}>
                                          {epic.name}
                                        </span>
                                      </div>
                                      {/* Epic Timeline Bar */}
                                      <div className="flex-1 h-10 relative px-2 py-2 group">
                                        <div 
                                          className={`absolute h-6 rounded ${epicStatusColor} opacity-80 flex items-center px-2 cursor-pointer transition-all hover:opacity-100 hover:shadow-md`}
                                          style={{ left: epicBarPos.left, width: epicBarPos.width, top: '50%', transform: 'translateY(-50%)', minWidth: '40px' }}
                                          title={`${epic.name}\nStatus: ${epic.status}\n${epic.feature_count || 0} features`}
                                        >
                                          <span className="text-xs text-white font-medium truncate">
                                            {epic.status}
                                          </span>
                                        </div>
                                        {/* Tooltip on hover */}
                                        <div className="absolute hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 z-20 whitespace-nowrap -top-8 left-1/2 transform -translate-x-1/2">
                                          {epic.name} • {epic.feature_count || 0} features
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Features under epic with Gantt Bars */}
                                    {epic.features?.map((feature) => {
                                      const featureBarPos = calculateBarPosition(feature.start_date, feature.end_date, timeline);
                                      const featureStatusColor = feature.status === 'Completed' ? 'bg-green-400' :
                                        feature.status === 'In Progress' ? 'bg-blue-400' :
                                        feature.status === 'Behind' ? 'bg-red-400' :
                                        feature.status === 'At Risk' ? 'bg-amber-400' : 'bg-gray-300';
                                      
                                      return (
                                        <div 
                                          key={feature.feature_id} 
                                          className="flex items-center border-b last:border-b-0 hover:bg-[var(--clr-surface-hover)]"
                                        >
                                          <div className="w-64 flex-shrink-0 p-2 pl-20 flex items-center gap-2">
                                            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                              Feature
                                            </span>
                                            <span className="text-xs text-[var(--clr-text-primary)] truncate" title={feature.name}>
                                              {feature.name}
                                            </span>
                                          </div>
                                          {/* Feature Timeline Bar */}
                                          <div className="flex-1 h-8 relative px-2 py-1 group">
                                            <div 
                                              className={`absolute h-4 rounded ${featureStatusColor} opacity-75 flex items-center px-1 cursor-pointer transition-all hover:opacity-100 hover:shadow-sm`}
                                              style={{ left: featureBarPos.left, width: featureBarPos.width, top: '50%', transform: 'translateY(-50%)', minWidth: '30px' }}
                                              title={`${feature.name}\nStatus: ${feature.status}${feature.end_date ? `\nDue: ${new Date(feature.end_date).toLocaleDateString()}` : ''}`}
                                            >
                                              <span className="text-[10px] text-white font-medium truncate">
                                                {feature.status === 'Completed' ? '✓' : feature.status.charAt(0)}
                                              </span>
                                            </div>
                                            {/* Tooltip on hover */}
                                            <div className="absolute hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 z-20 whitespace-nowrap -top-6 left-1/2 transform -translate-x-1/2">
                                              {feature.name} • {feature.status}
                                              {feature.end_date && ` • Due: ${new Date(feature.end_date).toLocaleDateString()}`}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })
                            ) : (
                              <div className="pl-16 py-3 text-sm text-[var(--clr-text-secondary)]">
                                No epics found for this project
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          ))}

          {/* Ungrouped Projects */}
          {roadmapData?.ungrouped_projects?.length > 0 && (
            <Card>
              <div className="flex items-center bg-[var(--clr-surface-subtle)]">
                <div className="w-64 flex-shrink-0 p-4 flex items-center gap-3">
                  <Folder className="w-5 h-5 text-[var(--clr-text-secondary)]" />
                  <div>
                    <p className="font-medium text-[var(--clr-text-secondary)]">Ungrouped Projects</p>
                    <p className="text-xs text-[var(--clr-text-secondary)]">
                      {roadmapData.ungrouped_projects.length} projects
                    </p>
                  </div>
                </div>
                <div className="flex-1" />
              </div>
              
              <div className="border-t">
                {roadmapData.ungrouped_projects.map((project) => {
                  const barPos = calculateBarPosition(project.start_date, project.end_date, timeline);
                  
                  return (
                    <div 
                      key={project.project_id}
                      className="flex items-center border-b last:border-b-0 hover:bg-[var(--clr-surface-subtle)]"
                    >
                      <div className="w-64 flex-shrink-0 p-3 pl-8 flex items-center gap-3">
                        <Folder className="w-4 h-4 text-[var(--clr-text-secondary)]" />
                        <div className="flex-1 min-w-0">
                          <Link 
                            to={`/project/${project.project_id}`}
                            className="font-medium text-sm text-[var(--clr-text-primary)] hover:text-[var(--clr-brand-primary)] truncate block"
                          >
                            {project.name}
                          </Link>
                          <div className="flex items-center gap-2 mt-1">
                            <div className={`w-2 h-2 rounded-full ${getHealthColor(project.health_status)}`} />
                            <span className="text-xs text-[var(--clr-text-secondary)]">{project.phase}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex-1 h-14 relative px-2 py-2">
                        <div 
                          className={`absolute h-8 rounded ${getHealthColor(project.health_status)} opacity-80 flex items-center px-2`}
                          style={{ left: barPos.left, width: barPos.width, top: '50%', transform: 'translateY(-50%)' }}
                        >
                          <span className="text-xs text-white font-medium truncate">
                            {project.progress_percent}%
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Empty State */}
          {(!roadmapData?.programs?.length && !roadmapData?.ungrouped_projects?.length) && (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-[var(--clr-text-secondary)] opacity-50" />
                <p className="text-[var(--clr-text-secondary)]">No projects found</p>
                <p className="text-sm text-[var(--clr-text-secondary)] mt-1">
                  Try adjusting your filters or add some projects
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Legend */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center gap-6 text-sm">
            <span className="text-[var(--clr-text-secondary)]">Health Status:</span>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span>On Track</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
              <span>At Risk</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span>Critical</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortfolioRoadmapPage;
